import { Component, OnInit } from '@angular/core';
import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { Table } from '../../components/table/table';
import {
  TableHeadSelectionComponent,
  TableRowSelectionComponent,
} from '../../components/select-column/select-column';
import { TefBoxService } from '../../services/tef-box-service';
import { DataPicker } from '../../components/data-picker/data-picker';
import { PaginationComponent } from '../../components/pagination-component/pagination-component';
import { formatCompactDateTime, parseCompactDateTime } from '../../utils/formatters';
import { Button } from '../../components/button/button';
import { Select } from '../../components/select/select';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MultiTextInput } from '../../components/multi-text-input/multi-text-input';
import { Loading } from '../../components/loading/loading';

type Person = {
  firstName: string;
  lastName: string;
  age: number;
  visits: number;
  status: string;
  progress: number;
};

@Component({
  selector: 'app-transactions',
  imports: [
    Table,
    DataPicker,
    PaginationComponent,
    Button,
    Select,
    FormsModule,
    ReactiveFormsModule,
    MultiTextInput,
    Loading,
  ],
  templateUrl: './transactions.html',
  styleUrl: './transactions.scss',
})
export class Transactions implements OnInit {
  loading = false;
  data: any[] = [];
  columns: ColumnDef<Person>[] = [
    {
      id: 'select-col',
      header: () => flexRenderComponent(TableHeadSelectionComponent),
      cell: () => flexRenderComponent(TableRowSelectionComponent),
    },
    {
      accessorKey: 'transaction.requestId',
      header: 'Transaction Id',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'transaction.requestTimestamp',
      header: 'Data',
      cell: (info) => {
        const value: string = `${info.getValue()}`;

        return parseCompactDateTime(value).toLocaleDateString();
      },
    },
    {
      accessorKey: 'result.amount',
      header: 'Valor',
      cell: (info) => {
        const amount: any = Number(info.getValue()) / 100;

        return Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
      },
    },
    {
      accessorKey: 'pos.posLabel',
      header: 'Terminal',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'transaction.type',
      header: 'Result',
      cell: (info) => this.getLabel(Number(info.getValue()), this.typeOptions),
    },
    {
      accessorKey: 'result.transactionResponse',
      header: 'Status',
      cell: (info) => this.getLabel(Number(info.getValue()), this.transactionResponseOptions),
    },
  ];

  rangeData: [Date, Date] = [new Date(), new Date()];
  page = 1;
  itemsPerPage = 10;
  total = 50;

  // filtro!: TransactionsFilter;
  filtroFormGroup = new FormGroup({
    startTimestamp: new FormControl<number>(0),
    endTimestamp: new FormControl<number>(0),
    type: new FormControl(),
    product: new FormControl(),
    transactionResponse: new FormControl(),
    pos: new FormControl<string[]>([]),
    page: new FormControl(),
    pageSize: new FormControl(),
  });

  typeOptions = [
    { value: 1, label: 'Pagamento' },
    { value: 2, label: 'Cancelamento (estorno)' },
    { value: 3, label: 'Configuração' },
    { value: 4, label: 'Parametrização' },
    { value: 5, label: 'Ativação do POS7' },
    { value: 6, label: 'Telecarga' },
    { value: 7, label: 'Teste de comunicação' },
    { value: 8, label: 'Fechamento' },
    { value: 9, label: 'Desativar terminal' },
    { value: 10, label: 'Relatório de vendas' },
    { value: 11, label: 'Abertura' },
    { value: 12, label: 'Enviar estatísticas' },
    { value: 13, label: 'Alterar senha do estabelecimento' },
    { value: 14, label: 'Consultar saldo' },
    { value: 15, label: 'Consulta parcelas' },
    { value: 16, label: 'Ligar WiFi' },
    { value: 17, label: 'Desligar WiFi' },
    { value: 18, label: 'Reimpressão' },
    { value: 19, label: 'Reenviar SMS' },
    { value: 20, label: 'Desfazimento' },
    { value: 21, label: 'Estorno Pix' },
    { value: 22, label: 'Consulta Pix' },
  ];
  productOptions = [
    { label: 'Crédito', value: 1 },
    { label: 'Débito', value: 2 },
    { label: 'Voucher', value: 4 },
    { label: 'PIX', value: 8 },
  ];
  transactionResponseOptions = [
    { value: 0, label: 'Transação aprovada.' },
    { value: 1, label: 'Transação negada' },
    { value: 2, label: 'Transação cancelada' },
    {
      value: 3,
      label: 'Transação falhou',
    },
    {
      value: 4,
      label: 'não configurada/parametrizada ou sem licença válida',
    },
    { value: 5, label: 'Transação indisponível nesta versão do POS7.' },
    {
      value: 6,
      label:
        'Desfazimento não permitido porque o id de transação informado não é o da última transação.',
    },
    {
      value: 7,
      label: 'O desfazimento não é necessário porque a transação original não foi aprovada.',
    },
    { value: 8, label: 'Não é mais possível realizar o desfazimento desta transação.' },
    { value: 9, label: 'Não é permitido realizar o desfazimento porque o POS já imprimiu o' },
  ];

  constructor(private tefBoxService: TefBoxService) {}

  ngOnInit(): void {
    this.rangeData[0].setHours(0);
    this.rangeData[1].setHours(23);
    this.rangeData[0].setDate(this.rangeData[0].getDate() - 1);

    this.filtroFormGroup.patchValue({
      startTimestamp: formatCompactDateTime(this.rangeData[0]),
      endTimestamp: formatCompactDateTime(this.rangeData[1]),
      page: 1,
      pageSize: this.itemsPerPage,
    });

    this.getDataTable();
  }

  onRangeChanged(value: [Date, Date]) {
    this.rangeData = value;
    this.filtroFormGroup.patchValue({
      startTimestamp: formatCompactDateTime(value[0]),
      endTimestamp: formatCompactDateTime(value[1]),
    });
  }

  onPageChange(newPage: number) {
    this.page = newPage;
    this.filtroFormGroup.patchValue({
      page: newPage,
    });
    this.getDataTable();
  }

  onItemsPerPageChange(limit: number) {
    this.itemsPerPage = limit;
    this.filtroFormGroup.patchValue({
      pageSize: limit,
    });
  }

  getDataTable() {
    this.loading = true;

    if (this.filtroFormGroup.invalid) {
      this.loading = false;
      return;
    }

    this.tefBoxService.getTransactions(this.filtroFormGroup.value).subscribe((resp) => {
      this.data = resp.results.data;
      this.page = resp.results.pageControl.page;
      this.total = resp.results.pageControl.totalPages * resp.results.pageControl.currentPageSize;
      this.itemsPerPage = resp.results.pageControl.currentPageSize;
      this.loading = false;
    });
  }

  filtrar() {
    this.getDataTable();
  }

  private getLabel(value: number, array: { value: number; label: string }[]) {
    return array.find((t) => t.value === value)?.label ?? 'Desconhecido';
  }
}
